import { Button, Col, Divider, FormModel, Icon, Input, Modal, Row, Select, Upload } from "ant-design-vue";
import { Component, Prop, Ref, Vue, Watch } from "vue-property-decorator";
import { getModule } from "vuex-module-decorators";
import { Drawer } from "../drawer";
import Loading from "../loading/loading.vue";
import { FocusWordsFormLocale, MediaUploaderLocale } from "@/locales/localeid";
import { VCSLevelModel } from "@/models/title-list-model";
import { VersionGroupModule } from "@/modules/versiongroup";
import TitleListState from "@/modules/title-list";
import { CurriculumType, MediaPlayerContentType, MediaSlot, MediaStatus, MediaType } from "@/util/enum";
import { MessageHelper, NotificationType, PrimaryLanguageLocale, ServerExceptionCode, fmtMsg } from "commonui";
import { FocusWordsManagerService } from "@/services/focus-words-manager-service";
import { cloneDeep, isEqual, isString, last, sortBy } from "lodash";
import { v4 } from "uuid";
import { ResourceService } from "@/services/resource";
import IPdfPagesViewer from "../pdf-pages-viewer/pdf-pages-viewer";
import PdfPagesViewer from "../pdf-pages-viewer/pdf-pages-viewer.vue";
import VideoJSPlayer from "@/components/videojsplayer/videojsplayer.vue";
import IVideoJSPlayer from "@/components/videojsplayer/videojsplayer";
import { MediaUploadState } from "@/modules/media-upload";
import { MediaSlotTypeMapForView } from "@/util/constants";

export interface FocusWordFormModel {
	id: number;
	text: string;
	focusWordDefinitions: FocusWordDefinitionInfo[];
}

export interface FocusWordDefinitionInfo {
	definition: string;
	focusWordMedia: any;
	id: string | null;
	isFocusWordDefinitionAdd: boolean;
	unit: number;
	unitId: number;
	versionGroupId: string;
	hasImageMedia: boolean;
	hasDefinitionAudioMedia: boolean;
	hasAudioMedia: boolean;
}

@Component({
	components: {
		Drawer,
		Button,
		FormModel,
		FormItem: FormModel.Item,
		Input,
		Select,
		Option: Select.Option,
		Divider,
		Row,
		Col,
		Loading,
		Icon,
		Upload,
		VideoJSPlayer,
		PdfPagesViewer,
		Modal,
		UploadDragger: Upload.Dragger
	}
})
export default class FocusWordsManagerForm extends Vue {
	@Prop({ default: () => -1 }) focusWordId!: number;
	@Prop({ default: () => 0 }) startUnitId!: number;
	@Prop({ default: () => false }) visible!: boolean;
	@Prop({ default: () => -1 }) initialUnit!: number;
	@Prop({ default: () => -1 }) initialUnitId!: number;
	@Prop({ default: () => "" }) lsId!: string;
	@Prop({ default: () => "" }) gsId!: string;
	@Prop() close!: Function;
	@Prop() setFocusWordId!: Function;
	@Prop() fetchUpdatedList!: Function;
	@Ref("formRef") formRef!: FormModel;
	@Ref("pdfPagesViewerRef") pdfPagesViewerRef!: IPdfPagesViewer;
	@Ref("videoPlayerRef") videoPlayerRef!: IVideoJSPlayer;
	private mediaUploadModule = getModule(MediaUploadState);
	public drawerTitle: string = "";
	public readonly locale = FocusWordsFormLocale;

	public titleListModule = getModule(TitleListState);
	public versionGroupModule = VersionGroupModule;
	public versionGroupId = "";
	public saveLoading: boolean = false;
	public MediaStatus = MediaStatus;
	public unitOccupied: { [key: number]: boolean } = {};
	public loading: boolean = false;
	public formData: FocusWordFormModel = { id: 0, text: "", focusWordDefinitions: [] };
	public isDataVisible: boolean = false;
	public removeAssociationModalVisible: boolean = false;
	public deleteFocusWordConfirmationModalVisible: boolean = false;
	public isRemoveDisabled: boolean = false;
	public isFocusWordDeleting: boolean = false;
	public componentList: string[] = [];
	public str: any = "";
	public enableSaveButton: boolean = false;
	public removedFocusWordDefinitionIds: string[] = [];
	public mediaSlotInfo: {
		[key: number]: {
			ext: string;
			icon: string;
			text: string;
			tooltip: string;
			handler: (item) => void;
			size: number;
		};
	} = {
		[MediaSlot.Audio]: {
			ext: ".mp3",
			icon: "play-circle",
			text: MediaSlotTypeMapForView[MediaSlot.Audio],
			tooltip: "Play Audio", // localize
			handler: this.viewMedia,
			size: 25 * 1024 * 1024
		},
		[MediaSlot.IndividualAudio]: {
			ext: ".mp3",
			icon: "play-circle",
			text: fmtMsg(this.locale.DefinitionAudioLabel),
			tooltip: "Play Audio", // localize
			handler: this.viewMedia,
			size: 25 * 1024 * 1024
		},
		[MediaSlot.Image]: {
			ext: ".png",
			icon: "picture",
			text: MediaSlotTypeMapForView[MediaSlot.Image],
			tooltip: "View Image", //localize
			handler: this.viewMedia,
			size: 5 * 1024 * 1024
		}
	};

	onEditFocusWord() {
		this.enableSaveButton = true;
	}

	get mediaUploaderLocale() {
		return MediaUploaderLocale;
	}

	public getMediaTypeName(str) {
		return str.split(".").join("").toUpperCase();
	}

	public async viewMedia(item?) {
		if (item?.mediaSlotId === MediaSlot.Audio || item?.mediaSlotId === MediaSlot.IndividualAudio) {
			if (isString(item?.fileUrl) && item?.mediaId) {
				const fileUrl = await ResourceService.getDownloadMediaUrl(item.mediaId);
				const result = this.videoPlayerRef.showModal(item.mediaId as string, MediaType.MP3, "Media Viewer");
				if (result) {
					setTimeout(() => {
						this.videoPlayerRef.setAWSMediaPlayer(
							item.mediaId as string,
							fileUrl,
							MediaPlayerContentType.mp3
						);
					}, 100);
				}
			}
		} else {
			if (isString(item?.fileUrl) && item?.mediaId) {
				this.pdfPagesViewerRef.open({
					mediaId: item.mediaId,
					mediaSlotId: MediaSlot.Image
				});
			}
		}
	}

	public removeMedia(item, mediaIndex: number) {
		const vpcMedia = item.focusWordMedia[mediaIndex];
		vpcMedia.isMediaRemoved = true;
		vpcMedia.fileUrl = null;
		vpcMedia.fileId = undefined;
		vpcMedia.isMediaAdded = false;
		vpcMedia.isMediaReplaced = false;
		this.onEditFocusWord();
	}

	private checkMediaInitialState() {
		this.formData.focusWordDefinitions.forEach((word) => {
			word.hasImageMedia =
				word.focusWordMedia.filter((media) => media.mediaSlotId === MediaSlot.Image && media.fileUrl !== null)
					.length > 0;

			word.hasDefinitionAudioMedia =
				word.focusWordMedia.filter(
					(media) => media.mediaSlotId === MediaSlot.IndividualAudio && media.fileUrl !== null
				).length > 0;

			word.hasAudioMedia =
				word.focusWordMedia.filter((media) => media.mediaSlotId === MediaSlot.Audio && media.fileUrl !== null)
					.length > 0;
		});
	}

	@Watch("visible")
	public getData() {
		if (this.visible == true && this.focusWordId != 0) {
			this.loading = true;
			const params = {
				id: this.focusWordId
			};
			const queryParams = {
				gsVersionGroupId:
					VersionGroupModule.selectedGsVersion !== "-1" ? VersionGroupModule.selectedGsVersion : null,
				lsVersionGroupId:
					VersionGroupModule.selectedLsVersion !== "-1" ? VersionGroupModule.selectedLsVersion : null
			};
			FocusWordsManagerService.getFocusWordData(params, queryParams).then((res: FocusWordFormModel) => {
				const indexOfStartUnit = res.focusWordDefinitions.findIndex((ele) => ele.unitId == this.startUnitId);
				const startUnitItem: any = res.focusWordDefinitions.find((ele) => ele.unitId == this.startUnitId);

				res.focusWordDefinitions.splice(indexOfStartUnit, 1);
				res.focusWordDefinitions.unshift(startUnitItem);

				this.formData = cloneDeep(res);
				const wordInfo = this.formData.focusWordDefinitions;
				const wordWithMedias = wordInfo.map((word) => {
					return { ...word, focusWordMedia: this.getFormattedVpcMedia(word.id, word.focusWordMedia) };
				});

				this.formData.focusWordDefinitions = wordWithMedias;
				this.checkMediaInitialState();
				this.occupiedUnitUpdate();
				this.loading = false;
				this.isRemoveDisabled = false;
			});
		} else {
			this.isRemoveDisabled = true;
			this.unitOccupied = {};
			this.setFormData();
		}
	}

	private getFormattedVpcMedia(id: string | null, focusWordMedias?: any[]): [any, any, any] {
		const audioSlot: any = {
			mediaId: "",
			mediaSlotId: MediaSlot.Audio,
			fileUrl: null,
			isMediaReplaced: false,
			isMediaRemoved: false,
			isMediaAdded: false,
			size: 0,
			length: 0,
			fileName: "",
			mediaTypeId: MediaType.MP3,
			languageCode: null,
			focusWordDefinitionId: id
		};
		const individualAudioSlot: any = {
			mediaId: "",
			mediaSlotId: MediaSlot.IndividualAudio,
			fileUrl: null,
			isMediaReplaced: false,
			isMediaRemoved: false,
			isMediaAdded: false,
			size: 0,
			length: 0,
			fileName: "",
			mediaTypeId: MediaType.MP3,
			languageCode: null,
			focusWordDefinitionId: id
		};
		const imageSlot: any = {
			mediaId: "",
			mediaSlotId: MediaSlot.Image,
			fileUrl: null,
			isMediaReplaced: false,
			isMediaRemoved: false,
			isMediaAdded: false,
			size: 0,
			length: 0,
			fileName: "",
			mediaTypeId: MediaType.PNG,
			languageCode: null,
			focusWordDefinitionId: id
		};

		if (focusWordMedias) {
			focusWordMedias.forEach((media) => {
				if (media.mediaSlotId === MediaSlot.Audio) {
					audioSlot.mediaId = media.mediaId;
					audioSlot.fileUrl = media.fileUrl;
					audioSlot.isMediaReplaced = media.isMediaReplaced;
					audioSlot.isMediaAdded = media.isMediaAdded;
					audioSlot.isMediaRemoved = media.isMediaRemoved;
					audioSlot.size = media.size;
					audioSlot.length = media.length;
					audioSlot.filename = media.fileName;
					audioSlot.mediaTypeId = media.mediaTypeId;
					audioSlot.languageCode = media.languageCode;
				} else if (media.mediaSlotId === MediaSlot.IndividualAudio) {
					individualAudioSlot.mediaId = media.mediaId;
					individualAudioSlot.fileUrl = media.fileUrl;
					individualAudioSlot.isMediaReplaced = media.isMediaReplaced;
					individualAudioSlot.isMediaAdded = media.isMediaAdded;
					individualAudioSlot.isMediaRemoved = media.isMediaRemoved;
					individualAudioSlot.size = media.size;
					individualAudioSlot.length = media.length;
					individualAudioSlot.filename = media.fileName;
					individualAudioSlot.mediaTypeId = media.mediaTypeId;
					individualAudioSlot.languageCode = media.languageCode;
				} else if (media.mediaSlotId === MediaSlot.Image) {
					imageSlot.mediaId = media.mediaId;
					imageSlot.fileUrl = media.fileUrl;
					imageSlot.isMediaReplaced = media.isMediaReplaced;
					imageSlot.isMediaAdded = media.isMediaAdded;
					imageSlot.isMediaRemoved = media.isMediaRemoved;
					imageSlot.size = media.size;
					imageSlot.length = media.length;
					imageSlot.filename = media.fileName;
					imageSlot.mediaTypeId = media.mediaTypeId;
					imageSlot.languageCode = media.languageCode;
				}
			});
		}
		return [audioSlot, individualAudioSlot, imageSlot];
	}

	public getLabel(index) {
		if (index) {
			return this.$format(this.locale.AdditionalUnitLabel);
		}
		return this.$format(this.locale.StartUnitLabel);
	}

	public get levels(): VCSLevelModel[] {
		/*
		 If selected GrapeSeed version is None the select only LittleSeed units.
		 If selected LittleSeed version is None the select only GrapeSeed units.
		 If both GrapeSeed and LittleSeed are None then return empty array.
		 If both GrapeSeed and LittleSeed are selected then return all the units.
		*/
		if (VersionGroupModule.selectedGsVersion == "-1" && VersionGroupModule.selectedLsVersion != "-1") {
			return this.titleListModule.levels.filter((l) => l.curriculumType != 0);
		} else if (VersionGroupModule.selectedLsVersion == "-1" && VersionGroupModule.selectedGsVersion != "-1") {
			return this.titleListModule.levels.filter((l) => l.curriculumType != 1);
		} else if (VersionGroupModule.selectedGsVersion == "-1" && VersionGroupModule.selectedLsVersion == "-1") {
			return [];
		} else {
			return this.titleListModule.levels;
		}
	}

	get modalTitle() {
		if (this.focusWordId) {
			return this.$format(this.locale.EditWordDrawerTitle);
		}
		return this.$format(this.locale.AddWordDrawerTitle);
	}

	public onDrawerClose() {
		this.close();
		this.enableSaveButton = false;
		this.setFocusWordId(0);
		this.saveLoading = false;
		this.removedFocusWordDefinitionIds = [];
	}

	onCancel() {
		this.onDrawerClose();
	}

	setFormData() {
		this.formData = {
			id: 0,
			text: "",
			focusWordDefinitions: [
				{
					definition: "",
					id: null,
					hasImageMedia: false,
					hasDefinitionAudioMedia: false,
					hasAudioMedia: false,
					focusWordMedia: [
						{
							mediaId: "",
							mediaSlotId: MediaSlot.Audio,
							fileUrl: null,
							isMediaReplaced: null,
							isMediaRemoved: null,
							isMediaAdded: null,
							size: 0,
							length: 0,
							fileName: "",
							mediaTypeId: MediaType.MP3,
							languageCode: null
						},
						{
							mediaId: "",
							mediaSlotId: MediaSlot.IndividualAudio,
							fileUrl: null,
							isMediaReplaced: null,
							isMediaRemoved: null,
							isMediaAdded: null,
							size: 0,
							length: 0,
							fileName: "",
							mediaTypeId: MediaType.MP3,
							languageCode: null
						},
						{
							mediaId: "",
							mediaSlotId: MediaSlot.Image,
							fileUrl: null,
							isMediaReplaced: null,
							isMediaRemoved: null,
							isMediaAdded: null,
							size: 0,
							length: 0,
							fileName: "",
							mediaTypeId: MediaType.PNG,
							languageCode: null
						}
					],
					isFocusWordDefinitionAdd: true,
					unit: this.getUnitForNewFocusWord(this.initialUnitId),
					unitId: this.initialUnitId,
					versionGroupId: this.getVersionGroupIdForNewFocusWord(this.initialUnitId)
				}
			]
		};
	}

	public addMedia($event: { file: File }, item) {
		const { file } = $event;

		if (file && this.validateFile(file, item.mediaSlotId) && this.validateFileSize(file, item.mediaSlotId)) {
			// Find the concerned FocusWordDefinitionInfo using item.focusWordDefinitionId
			const focusWordDefinition = this.formData.focusWordDefinitions.find(
				(fwd) => fwd.id === item.focusWordDefinitionId
			);

			// Identify the media type: Image, Definition Audio, or Audio based on mediaSlotId
			const isImageMedia = item.mediaSlotId === MediaSlot.Image;
			const isDefinitionAudioMedia = item.mediaSlotId === MediaSlot.IndividualAudio;
			const isAudioMedia = item.mediaSlotId === MediaSlot.Audio;

			// Get the initial state of media from the found FocusWordDefinitionInfo
			let isMediaInitiallyPresent = false;

			if (isImageMedia) {
				isMediaInitiallyPresent = focusWordDefinition?.hasImageMedia ?? false;
			} else if (isDefinitionAudioMedia) {
				isMediaInitiallyPresent = focusWordDefinition?.hasDefinitionAudioMedia ?? false;
			} else if (isAudioMedia) {
				isMediaInitiallyPresent = focusWordDefinition?.hasAudioMedia ?? false;
			}

			// If media was removed earlier, check the initial state
			if (item.isMediaRemoved) {
				// If it was initially present, this is a modification (remove -> add)
				if (isMediaInitiallyPresent) {
					item.isMediaReplaced = true;
					item.isMediaAdded = false;
				}
				// If it was not initially present, it's a net addition (add -> remove -> add)
				else {
					item.isMediaAdded = true;
					item.isMediaReplaced = false;
					item.fileId = v4();
				}
				item.isMediaRemoved = false;
				item.fileUrl = file;
			}
			// Handle media replacement case
			else if (item.isMediaReplaced || (isString(item.fileUrl) && !item.isMediaAdded)) {
				item.isMediaReplaced = true;
				item.isMediaAdded = false;
				item.isMediaRemoved = false;
				item.fileUrl = file;
			}
			// Handle media addition case
			else {
				item.isMediaAdded = true;
				item.isMediaReplaced = false;
				item.isMediaRemoved = false;
				item.fileUrl = file;
				item.fileId = v4();
			}
			this.onEditFocusWord();
		}
	}

	public isFile(fileUrl?: string | File | null): boolean {
		return fileUrl instanceof File;
	}

	public getFileName(fileUrl: string | File | null): string {
		if (fileUrl instanceof File) {
			return fileUrl.name;
		}
		return "";
	}

	private validateFile(file: File, mediaSlot: MediaSlot): boolean {
		let fileExtension = file.name.split(".").pop()!;
		fileExtension = fileExtension.toLowerCase();

		const expectedExtension = this.mediaSlotInfo[mediaSlot].ext;
		if (expectedExtension === `.${fileExtension}`) {
			return true;
		}

		MessageHelper.Message(
			NotificationType.Warning,
			this.$format(this.locale.MediaInvalid, { extension: expectedExtension })
		);

		return false;
	}

	private validateFileSize(file: File, mediaSlot: MediaSlot): boolean {
		const fileSize = file.size;

		const expectedFileSize = this.mediaSlotInfo[mediaSlot].size;
		if (fileSize <= expectedFileSize) {
			return true;
		}

		const size = mediaSlot == MediaSlot.Image ? 5 : 25;
		MessageHelper.Message(NotificationType.Warning, this.$format(this.locale.MediaSizeInvalid, { size: size }));

		return false;
	}

	public occupiedUnitUpdate() {
		const newUnitOccupied = {};
		this.formData.focusWordDefinitions.forEach((item) => {
			if (item.unitId) {
				newUnitOccupied[item.unitId] = true;
			}
			item.unit = this.getUnitForNewFocusWord(item.unitId);
			item.versionGroupId = this.getVersionGroupIdForNewFocusWord(item.unitId);
		});
		this.unitOccupied = newUnitOccupied;
	}

	public handleOccupiedUnitUpdate() {
		this.occupiedUnitUpdate();
		this.onEditFocusWord();
	}

	private getVersionGroupIdForNewFocusWord(unitId) {
		const unit = this.levels.find((level) => level.id === unitId)!;
		if (unit.curriculumType === CurriculumType.GrapeSEED) {
			return VersionGroupModule.selectedGsVersion;
		}
		return VersionGroupModule.selectedLsVersion;
	}

	private getUnitIdForNewFocusWord(unitId?: number): number {
		const sortedUnits = sortBy(this.levels, "curriculumType", "unit");
		const lastUnitId = unitId ? unitId : last(this.formData.focusWordDefinitions)?.unitId;
		const indexOfLastVpcUnit = sortedUnits.findIndex((level) => level.id === lastUnitId);
		const isLastUnit = indexOfLastVpcUnit === sortedUnits.length - 1;

		let nextUnitId: number | null = null;

		if (isLastUnit) {
			nextUnitId = sortedUnits[0].id;
		} else {
			nextUnitId = sortedUnits[indexOfLastVpcUnit + 1].id;
		}

		const isNextUnitIdAlreadyUsed = this.unitOccupied[nextUnitId];

		if (!isNextUnitIdAlreadyUsed) {
			return nextUnitId;
		} else {
			return this.getUnitIdForNewFocusWord(nextUnitId);
		}
	}

	private getUnitForNewFocusWord(unitId?: number): number {
		return this.levels.find((level) => level.id === unitId)?.unit || 0;
	}

	public onDelete() {
		this.deleteFocusWordConfirmationModalVisible = true;
	}

	public onDeleteOk() {
		this.isFocusWordDeleting = true;
		FocusWordsManagerService.deleteFocusWord({
			id: this.focusWordId
		})
			.then((res) => {
				MessageHelper.Message(NotificationType.Success, fmtMsg(this.locale.WordDeletedSuccessfullyMessage));
				this.isFocusWordDeleting = false;
				this.deleteFocusWordConfirmationModalVisible = false;
				this.fetchUpdatedList();
				this.close();
			})
			.catch((err) => {
				this.str = err.body.error_description[0];
				this.str = this.str.replace("Error:", " ");
				this.str = JSON.parse(this.str);

				this.str.forEach((ele: any) => {
					this.componentList.push(ele.title);
				});
				this.deleteFocusWordConfirmationModalVisible = false;
				this.removeAssociationModalVisible = true;
				this.isFocusWordDeleting = false;
			});
	}

	public onDeleteCancel() {
		this.deleteFocusWordConfirmationModalVisible = false;
	}

	public onModalOk() {
		this.removeAssociationModalVisible = false;
		this.componentList = [];
	}

	getLinkMessage(ele, index) {
		return fmtMsg(this.locale.RemoveAssociationsModalLinkMessage, {
			componentName: this.componentList[index]
		});
	}

	public onSave() {
		this.saveLoading = true;
		this.formRef.validate((isValid) => {
			if (isValid) {
				this.uploadFiles()
					.then(() => {
						FocusWordsManagerService.addUpdateFocusWord(this.formData)
							.then((res) => {
								if (res === ServerExceptionCode.DulicateWordExeption) {
									MessageHelper.Message(
										NotificationType.Warning,
										fmtMsg(this.locale.WordAlreadyExistsMessage)
									);
								} else {
									if (this.focusWordId) {
										MessageHelper.Message(
											NotificationType.Success,
											fmtMsg(this.locale.WordEditedSuccessfullyMessage)
										);
									} else {
										MessageHelper.Message(
											NotificationType.Success,
											fmtMsg(this.locale.WordAddedSuccessfullyMessage)
										);
									}
									this.fetchUpdatedList();
									this.onDrawerClose();
								}
								this.saveLoading = false;
							})
							.catch((err) => {
								const error = JSON.parse(err.text);
								this.saveLoading = false;

								if (error && error.error_code == ServerExceptionCode.UpdateFocusWordUnitException) {
									MessageHelper.Message(
										NotificationType.Warning,
										fmtMsg(this.locale.UnitChangeFailedMessage)
									);
								} else if (
									error &&
									error.error_code == ServerExceptionCode.DeleteFocusWordDefinitionException
								) {
									const unitIds = this.extractIntegersFromString(error.error_description[0]);
									const unitNames = unitIds.map((unitId) => {
										return this.titleListModule.levels.filter((obj) => obj.id == unitId)[0]
											.displayName;
									});
									MessageHelper.Message(
										NotificationType.Failed,
										fmtMsg(this.locale.FocusWordDefinitionDeleteFailedMessage, {
											units: unitNames.join(", ")
										})
									);
									this.fetchUpdatedList();
									this.onDrawerClose();
								} else {
									console.log(err);
								}
							});
					})
					.catch(() => {
						this.saveLoading = false;
						console.error("File upload error...");
					});
			} else {
				this.saveLoading = false;
			}
		});
	}

	extractIntegersFromString(str) {
		return str.match(/\d+/g).map(Number);
	}

	private uploadFiles(): Promise<void> {
		return new Promise((resolve, reject) => {
			const medias = this.formData.focusWordDefinitions;

			const filesToUpload: Promise<void>[] = [];

			medias.forEach((m) => {
				m.focusWordMedia.forEach((media) => {
					if (media.fileUrl instanceof File) {
						const uploaServiceCall = this.mediaUploadModule
							.uploadFile({
								id: media.fileId!,
								file: media.fileUrl!,
								slotId: media.mediaSlotId,
								languageCode: PrimaryLanguageLocale.English
							})
							.then(() => {
								const uploadedFile = this.mediaUploadModule.fileUploads.find(
									(file) => file.id === media.fileId
								)!;

								media.fileUrl = uploadedFile.filePath;
								media.size = uploadedFile.size;
								media.isUploading = false;
							});
						media.isUploading = true;
						filesToUpload.push(uploaServiceCall);
					}
				});
			});

			if (filesToUpload.length) {
				return Promise.all(filesToUpload)
					.then(() => {
						resolve();
					})
					.catch(() => {
						reject();
					});
			} else {
				resolve();
			}
		});
	}

	addAdditionalMediaCard() {
		const newUnitId = this.getUnitIdForNewFocusWord();
		const newUnit = this.getUnitForNewFocusWord(newUnitId);
		const versionGroup = this.getVersionGroupIdForNewFocusWord(newUnitId);
		this.formData.focusWordDefinitions.push({
			definition: "",
			id: null,
			hasImageMedia: false,
			hasDefinitionAudioMedia: false,
			hasAudioMedia: false,
			focusWordMedia: [
				{
					mediaId: "",
					mediaSlotId: MediaSlot.Audio,
					fileUrl: null,
					isMediaReplaced: false,
					isMediaRemoved: false,
					isMediaAdded: false,
					size: 0,
					length: 0,
					fileName: "",
					mediaTypeId: MediaType.MP3,
					languageCode: null
				},
				{
					mediaId: "",
					mediaSlotId: MediaSlot.IndividualAudio,
					fileUrl: null,
					isMediaReplaced: false,
					isMediaRemoved: false,
					isMediaAdded: false,
					size: 0,
					length: 0,
					fileName: "",
					mediaTypeId: MediaType.MP3,
					languageCode: null
				},
				{
					mediaId: "",
					mediaSlotId: MediaSlot.Image,
					fileUrl: null,
					isMediaReplaced: false,
					isMediaRemoved: false,
					isMediaAdded: false,
					size: 0,
					length: 0,
					fileName: "",
					mediaTypeId: MediaType.PNG,
					languageCode: null
				}
			],
			isFocusWordDefinitionAdd: true,
			unit: newUnit,
			unitId: newUnitId,
			versionGroupId: versionGroup
		});
		this.occupiedUnitUpdate();
		this.onEditFocusWord();
	}

	removeAdditionalMediaCard(item) {
		this.removedFocusWordDefinitionIds.push(item.id);
		this.formData.focusWordDefinitions = cloneDeep(
			this.formData.focusWordDefinitions.filter((obj) => !isEqual(obj, item))
		);
		this.onEditFocusWord();
	}

	getLabelForView(media) {
		if (media.mediaSlotId == MediaSlot.Audio || media.mediaSlotId == MediaSlot.IndividualAudio) {
			return this.$format(MediaUploaderLocale.MediaUploaderButtonListen);
		}
		return this.$format(MediaUploaderLocale.MediaUploaderButtonView);
	}
}
